import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, NOMBRES, conditions, excelDate, seededRandom } from "../spec";

const SHEET = "TX ERP";
const TABLE = "Exportacion";

const rnd = seededRandom(8080);
const sinAcentos = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
const pad = (n: number, size: number) => String(n).padStart(size, "0");

const BASE = NOMBRES.slice(0, 26).map((nombre, i) => {
  const [first, last] = nombre.split(" ");
  const correo = i % 8 === 3 ? `${sinAcentos(first)}.${sinAcentos(last)}.empresa.mx` : `${sinAcentos(first)}.${sinAcentos(last)}@empresa.mx`;
  const monto = i % 9 === 6 ? 0 : rnd.int(4, 960) * 25;
  const fecha = excelDate("2026-01-02") + rnd.int(0, 150);
  return { id: rnd.int(1, 9999), nombre, correo, monto, fecha };
});

const iso = (serial: number) => new Date(Date.UTC(1899, 11, 30) + serial * 86400000).toISOString().slice(0, 10);

// One dirty, pipe-separated record per row, as the old ERP exports it.
const ROWS = BASE.map((b) => {
  const [first, last] = b.nombre.split(" ");
  const nombreSucio = `${rnd.next() < 0.5 ? first.toUpperCase() : first.toLowerCase()}  ${rnd.next() < 0.5 ? last.toUpperCase() : last.toLowerCase()}`;
  const correoSucio = rnd.next() < 0.5 ? ` ${b.correo.toUpperCase()} ` : b.correo.replace("empresa", "Empresa");
  return [` ${pad(b.id, 5)}|${nombreSucio}|${correoSucio}|$${b.monto.toLocaleString("en-US")}| ${iso(b.fecha)} `] as [string];
});

export const spec: FuncionProjectSpec = {
  projectId: "funciones-erp",
  sheets: [
    {
      name: SHEET,
      title: "Exportación del ERP",
      subtitle: "Cada registro trae ID | nombre | correo | monto | fecha en una sola celda, con espacios y mayúsculas al azar",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Registro"],
          rows: ROWS,
          textColumns: ["Registro"],
          answerColumns: ["ID", "Cliente", "Correo", "Monto", "Fecha", "Estatus"],
          formats: { Monto: "$#,##0", Fecha: "dd/mm/yyyy" },
        },
      ],
    },
  ],
  checks: {
    1: {
      kind: "column", sheet: SHEET, table: TABLE, column: "ID", fns: ["VALUE", "TEXTBEFORE"],
      expected: BASE.map((b) => b.id),
    },
    2: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Cliente", fns: ["PROPER", "TRIM", "TEXTSPLIT|TEXTAFTER"],
      exact: true,
      expected: BASE.map((b) => b.nombre),
    },
    3: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Correo", fns: ["LOWER", "TRIM", "TEXTSPLIT|TEXTAFTER"],
      exact: true,
      expected: BASE.map((b) => b.correo),
    },
    4: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Monto", fns: ["VALUE", "SUBSTITUTE"],
      expected: BASE.map((b) => b.monto),
    },
    5: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Fecha", fns: ["DATE", "TEXTAFTER"],
      expected: BASE.map((b) => b.fecha),
    },
    6: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Estatus", fns: ["IF", "AND", "ISNUMBER", "SEARCH"],
      exact: true,
      expected: BASE.map((b) => (b.correo.includes("@") && b.monto > 0 ? "Válido" : "Revisar")),
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;
const PARTE = (n: number) => `INDICE(DIVIDIRTEXTO([@Registro],"|"),${n})`;
const FECHA_TXT = 'ESPACIOS(TEXTODESPUES([@Registro],"|",-1))';
const FECHA_FORMULA = `=FECHA(IZQUIERDA(${FECHA_TXT},4),EXTRAE(${FECHA_TXT},6,2),EXTRAE(${FECHA_TXT},9,2))`;

export const project: ProjectDefinition = {
  id: "funciones-erp",
  title: "Reto final: Exportación del ERP",
  subtitle: "Todas las funciones de texto en un caso real",
  description:
    "El ERP de la empresa exporta cada cliente en una sola celda: \" 00457|ANA  lópez| ANA.LOPEZ@EMPRESA.MX |$12,500| 2026-03-15 \". Tu trabajo es convertir esa columna en una tabla limpia: ID numérico, nombre bien escrito, correo en minúsculas, monto como número, fecha real y un estatus de validación.\n\nCada columna combina separar, limpiar y convertir.",
  topics: [
    "Tomar la parte N con INDICE(DIVIDIRTEXTO(...), N)",
    "Limpiar cada parte: ESPACIOS, NOMPROPIO, MINUSC",
    "Texto → número y texto → fecha",
    "Validar con SI, Y y ESNUMERO(HALLAR(...))",
  ],
  tasks: [
    {
      id: 1,
      level: "intermedio",
      title: "Tarea 1: ID numérico",
      description:
        "En la hoja <strong>TX ERP</strong>, columna <strong>ID</strong>: el ID es lo que está antes del primer <strong>|</strong>, con ceros y espacios. Conviértelo en número (\" 00457\" → <strong>457</strong>).",
      solution: [
        `Selecciona ${F("B5")}.`,
        "VALOR ignora los espacios de los extremos y los ceros de la izquierda.",
        `Escribe ${F('=VALOR(TEXTOANTES([@Registro],"|"))')}.`,
      ],
      expectedResult: "457",
      finalFormula: '=VALOR(TEXTOANTES([@Registro],"|"))',
    },
    {
      id: 2,
      level: "intermedio",
      title: "Tarea 2: Nombre del cliente",
      description: conditions("En la columna <strong>Cliente</strong>: toma la <strong>2.ª parte</strong> del registro y déjala como <strong>Ana López</strong>:", [
        "Separa el registro por <strong>|</strong> y toma la parte 2 con <strong>INDICE</strong>",
        "Quita los espacios dobles con <strong>ESPACIOS</strong>",
        "Mayúscula inicial con <strong>NOMPROPIO</strong>",
      ]),
      solution: [
        `Selecciona ${F("C5")}.`,
        `${F(PARTE(2))} devuelve solo la 2.ª parte del registro separado.`,
        `Escribe ${F(`=NOMPROPIO(ESPACIOS(${PARTE(2)}))`)}.`,
      ],
      expectedResult: "Ana López",
      finalFormula: `=NOMPROPIO(ESPACIOS(${PARTE(2)}))`,
    },
    {
      id: 3,
      level: "intermedio",
      title: "Tarea 3: Correo en minúsculas",
      description: "En la columna <strong>Correo</strong>: la <strong>3.ª parte</strong> del registro, sin espacios y en <strong>minúsculas</strong>.",
      solution: [`Selecciona ${F("D5")}.`, `Escribe ${F(`=MINUSC(ESPACIOS(${PARTE(3)}))`)}.`],
      expectedResult: "ana.lopez@empresa.mx",
      finalFormula: `=MINUSC(ESPACIOS(${PARTE(3)}))`,
    },
    {
      id: 4,
      level: "reto",
      title: "Tarea 4: Monto como número",
      description: conditions("En la columna <strong>Monto</strong>: la <strong>4.ª parte</strong> (\"$12,500\") como número:", [
        "Toma la parte 4",
        "Quita <strong>$</strong> y <strong>,</strong> con SUSTITUIR",
        "Convierte con <strong>VALOR</strong>",
      ]),
      solution: [
        `Selecciona ${F("E5")}.`,
        `Escribe ${F(`=VALOR(SUSTITUIR(SUSTITUIR(${PARTE(4)},"$",""),",",""))`)}.`,
      ],
      expectedResult: "12500",
      finalFormula: `=VALOR(SUSTITUIR(SUSTITUIR(${PARTE(4)},"$",""),",",""))`,
    },
    {
      id: 5,
      level: "reto",
      title: "Tarea 5: Fecha real",
      description: conditions(
        "En la columna <strong>Fecha</strong>: la última parte (\" 2026-03-15 \") es texto con formato AAAA-MM-DD. Conviértela en una fecha real:",
        [
          "Toma lo que está después del <strong>último |</strong> y quítale los espacios",
          "Año = 4 primeros caracteres, mes = caracteres 6 y 7, día = caracteres 9 y 10",
          "Únelos con <strong>FECHA</strong>",
        ]
      ),
      solution: [
        `Selecciona ${F("F5")}.`,
        `${F(FECHA_TXT)} da \"2026-03-15\". Repítelo dentro de cada IZQUIERDA y EXTRAE.`,
        `Escribe ${F(FECHA_FORMULA)}.`,
        "No uses FECHANUMERO: el resultado depende de la configuración regional de cada computadora; FECHA siempre funciona.",
      ],
      expectedResult: "15/03/2026 (fecha real)",
      finalFormula: FECHA_FORMULA,
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Estatus del registro",
      description: conditions("En la columna <strong>Estatus</strong>: el registro es <strong>Válido</strong> solo si se cumplen <strong>las dos</strong> condiciones; si no, <strong>Revisar</strong>:", [
        "El <strong>Correo</strong> limpio contiene <strong>@</strong>",
        "El <strong>Monto</strong> es mayor que <strong>0</strong>",
      ]),
      solution: [
        `Selecciona ${F("G5")}.`,
        "Usa las columnas que ya limpiaste: es mucho más fácil validar datos limpios.",
        `Escribe ${F('=SI(Y(ESNUMERO(HALLAR("@",[@Correo])),[@Monto]>0),"Válido","Revisar")')}.`,
      ],
      expectedResult: "Válido / Revisar",
      finalFormula: '=SI(Y(ESNUMERO(HALLAR("@",[@Correo])),[@Monto]>0),"Válido","Revisar")',
    },
  ],
};
